import { radius, shadow, space, minTouchTarget } from './layout.ts';
import { semanticColor } from './color.ts';
import { fontWeight } from './typography.ts';

export const button = {
  primary: {
    background: semanticColor.brandPrimary,
    backgroundHover: semanticColor.brandPrimaryHover,
    text: semanticColor.textOnBrand,
    /*
     * White on the approved Pink is 3.39:1. Primary CTA labels must therefore
     * qualify as WCAG large text: at least 18.66px bold.
     */
    fontSize: '1.1875rem',
    fontWeight: 700,
    border: 'transparent',
    radius: radius.pill,
    shadow: shadow.sm,
    focus: shadow.brandFocus,
  },
  secondary: {
    background: semanticColor.surfaceCard,
    backgroundHover: semanticColor.surfaceSubtle,
    text: semanticColor.brandContrast,
    border: semanticColor.borderStrong,
    radius: radius.pill,
    shadow: shadow.none,
    focus: shadow.focus,
  },
  plum: {
    background: semanticColor.brandContrast,
    backgroundHover: '#2C0733',
    text: semanticColor.textOnPlum,
    border: 'transparent',
    radius: radius.pill,
    shadow: shadow.sm,
    focus: shadow.focus,
  },
  ghost: {
    background: 'transparent',
    backgroundHover: semanticColor.surfaceSubtle,
    text: semanticColor.brandContrast,
    border: 'transparent',
    radius: radius.pill,
    shadow: shadow.none,
    focus: shadow.focus,
  },
  danger: {
    background: semanticColor.statusDanger,
    backgroundHover: '#A9203D',
    text: semanticColor.textOnBrand,
    border: 'transparent',
    radius: radius.pill,
    shadow: shadow.sm,
    focus: shadow.focus,
  },
  size: {
    sm: { height: '36px', paddingX: space[4], fontSize: '0.875rem' },
    md: { height: minTouchTarget, paddingX: space[5], fontSize: '1rem' },
    lg: { height: '52px', paddingX: space[6], fontSize: '1.0625rem' },
  },
  weight: fontWeight.semibold,
} as const;

export const price = {
  /*
   * The approved Pink is reserved for the active sale price so a discount keeps
   * its brand cue. Pink on white measures 3.39:1, which is below the 4.5:1
   * normal-text minimum but above the 3:1 large-text minimum, so the sale price
   * must be rendered as WCAG large text: at least 18.66px bold. Shrinking it
   * below this while keeping Pink would fail WCAG 2.2 AA 1.4.3.
   */
  sale: {
    color: semanticColor.brandPrimary,
    fontSize: '1.1875rem',
    fontWeight: 700,
  },
  regular: {
    color: semanticColor.textPrimary,
    fontSize: '1rem',
    fontWeight: 700,
  },
  /** Struck-through original price; muted on white is 5.85:1. */
  original: {
    color: semanticColor.textSecondary,
    fontSize: '0.8125rem',
  },
} as const;

export const input = {
  background: semanticColor.surfaceCard,
  text: semanticColor.textPrimary,
  placeholder: semanticColor.textSecondary,
  border: semanticColor.borderDefault,
  borderHover: semanticColor.borderStrong,
  borderFocus: semanticColor.brandContrast,
  borderError: semanticColor.statusDanger,
  radius: radius.md,
  height: minTouchTarget,
  paddingX: space[3],
  focus: shadow.focus,
} as const;

export const card = {
  background: semanticColor.surfaceCard,
  border: semanticColor.borderDefault,
  radius: radius.xl,
  shadow: shadow.sm,
  padding: space[5],
} as const;

export const productCard = {
  background: semanticColor.surfaceCard,
  radius: radius.xl,
  shadow: shadow.sm,
  imageRatio: '1 / 1',
  /** Clamps the media block so a wide card cannot scale its image into a
   *  full-width square that pushes the name, price and seller below the fold. */
  imageMaxHeight: '260px',
  imageRadius: radius.lg,
  gap: space[2],
  padding: space[4],
} as const;

export const storeCard = {
  background: semanticColor.surfaceCard,
  radius: radius.xl,
  shadow: shadow.xs,
  bannerRatio: '16 / 9',
  padding: space[4],
} as const;

export const badge = {
  radius: radius.pill,
  paddingX: space[2],
  paddingY: space[1],
  variant: {
    seller: { background: semanticColor.surfaceSubtle, text: semanticColor.brandContrast },
    openNow: {
      background: semanticColor.statusSuccessSurface,
      text: semanticColor.statusSuccessText,
    },
    openLate: {
      background: semanticColor.statusWarningSurface,
      text: semanticColor.statusWarningText,
    },
    overnight: { background: semanticColor.brandLavender, text: semanticColor.brandContrast },
    membership: { background: semanticColor.brandContrast, text: semanticColor.textOnPlum },
    sale: { background: semanticColor.brandPrimary, text: semanticColor.textOnBrand },
    freeDelivery: {
      background: semanticColor.statusSuccessSurface,
      text: semanticColor.statusSuccessText,
    },
    inStock: {
      background: semanticColor.statusSuccessSurface,
      text: semanticColor.statusSuccessText,
    },
    lowStock: {
      background: semanticColor.statusWarningSurface,
      text: semanticColor.statusWarningText,
    },
    criticalStock: {
      background: semanticColor.statusDangerSurface,
      text: semanticColor.statusDangerText,
    },
    outOfStock: { background: semanticColor.surfaceSubtle, text: semanticColor.textSecondary },
    neutral: { background: semanticColor.surfaceSubtle, text: semanticColor.textPrimary },
    info: { background: semanticColor.statusInfoSurface, text: semanticColor.statusInfo },
    success: {
      background: semanticColor.statusSuccessSurface,
      text: semanticColor.statusSuccessText,
    },
    warning: {
      background: semanticColor.statusWarningSurface,
      text: semanticColor.statusWarningText,
    },
    danger: { background: semanticColor.statusDangerSurface, text: semanticColor.statusDangerText },
  },
} as const;

export const table = {
  headerBackground: semanticColor.surfaceSubtle,
  headerText: semanticColor.textSecondary,
  rowBorder: semanticColor.borderDefault,
  rowHover: semanticColor.surfaceSubtle,
  cellPaddingX: space[4],
  cellPaddingY: space[3],
  radius: radius.lg,
} as const;

export const navigation = {
  customerHeader: {
    background: semanticColor.surfaceCard,
    text: semanticColor.textPrimary,
    border: semanticColor.borderDefault,
    height: '72px',
  },
  customerBottomNav: {
    background: semanticColor.surfaceCard,
    text: semanticColor.textSecondary,
    activeText: semanticColor.brandPrimary,
    border: semanticColor.borderDefault,
    height: '64px',
  },
  portalSidebar: {
    background: semanticColor.brandContrast,
    text: 'rgba(255,255,255,0.78)',
    activeText: semanticColor.surfaceWhite,
    activeBackground: 'rgba(255,255,255,0.12)',
    width: '264px',
  },
  portalHeader: {
    background: semanticColor.surfaceCard,
    border: semanticColor.borderDefault,
    height: '64px',
  },
} as const;

export const dialog = {
  background: semanticColor.surfaceCard,
  radius: radius['2xl'],
  shadow: shadow.lg,
  overlay: 'rgba(32, 18, 36, 0.55)',
  padding: space[6],
  maxWidth: '560px',
} as const;

export const metricCard = {
  background: semanticColor.surfaceCard,
  border: semanticColor.borderDefault,
  radius: radius.lg,
  padding: space[5],
  labelColor: semanticColor.textSecondary,
  valueColor: semanticColor.textPrimary,
} as const;
